import React, { useState, useMemo } from 'react';
import {
  Calculator, Search, TrendingUp, Award,
  AlertTriangle, CheckCircle, XCircle, X, RefreshCw,
  Eye, Pencil, Wand2, Printer,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import ScrollToTop from '../../components/ui/ScrollToTop';
import {
  useGetResultatsSemestreQuery,
  useCalculerToutNiveauMutation,
} from '../../services/resultatsApi';
import { useGetChoixFormulaireQuery } from '../../services/etudiantsApi';
import { useGetAnneesAcademiquesQuery } from '../../services/anneeAcademiqueApi';
import ModalReleveProvisoire from './ModalReleveProvisoire';
import ModalRachatIntelligent from './ModalRachatIntelligent';
import ModalSaisieNote from './ModalSaisieNote';

export const ResultatsList: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    niveau: '',
    semestre: '',
    decision: '',
    classe: '',
    pole: '',
    annee_academique: '',
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  // État modals
  const [modalReleve, setModalReleve] = useState<null | { matricule: string; semestre: number; annee: string }>(null);
  const [modalRachat, setModalRachat] = useState<null | {
    etudiantId: number; matricule: string; nom: string; niveau: string; classe: string;
    semestre: number; moyenneActuelle: number | null;
  }>(null);
  const [modalSaisie, setModalSaisie] = useState<null | {
    etudiantId: number; matricule: string; nom: string; niveau: string;
    semestre: number; annee: string; option?: string;
  }>(null);

  const { data: annees = [] } = useGetAnneesAcademiquesQuery({});
  const { data: choixFormulaire } = useGetChoixFormulaireQuery();
  const [calculerNiveau, { isLoading: calculating }] = useCalculerToutNiveauMutation();

  // Au moins un filtre requis (niveau ou classe) pour lancer la requête
  const hasRequiredFilter = !!(filters.niveau || filters.classe);

  // Construire les params pour l'API
  const apiParams = useMemo(() => {
    const params: Record<string, any> = {};
    if (filters.niveau) params.etudiant__niveau = filters.niveau;
    if (filters.semestre) params.semestre = parseInt(filters.semestre);
    if (filters.decision === 'admis') params.valide = true;
    if (filters.decision === 'ajourne') params.valide = false;
    if (filters.classe) params.etudiant__classe = filters.classe;
    if (filters.pole) params.etudiant__pole = filters.pole;
    if (filters.annee_academique) params.annee_academique = filters.annee_academique;
    if (searchQuery.trim()) params.search = searchQuery.trim();
    return params;
  }, [filters, searchQuery]);

  const { data: resultats = [], isLoading, refetch } = useGetResultatsSemestreQuery(
    apiParams,
    { skip: !hasRequiredFilter }
  );

  // Stats calculées à partir des données réelles
  const stats = useMemo(() => {
    if (!resultats.length) return { total: 0, admis: 0, ajournes: 0, moyenneGenerale: 0, tauxReussite: 0 };
    const admis = resultats.filter(r => r.valide).length;
    const ajournes = resultats.length - admis;
    const moyennes = resultats
      .map(r => r.moyenne_semestre != null ? parseFloat(String(r.moyenne_semestre)) : null)
      .filter((m): m is number => m !== null);
    const moyenneGenerale = moyennes.length > 0
      ? moyennes.reduce((s, v) => s + v, 0) / moyennes.length
      : 0;
    const tauxReussite = resultats.length > 0 ? (admis / resultats.length) * 100 : 0;
    return { total: resultats.length, admis, ajournes, moyenneGenerale, tauxReussite };
  }, [resultats]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    refetch();
  };

  const handleCalculerResultats = async () => {
    const niveau = filters.niveau;
    if (!niveau) {
      setMessage({ type: 'error', text: 'Veuillez sélectionner un niveau avant de lancer le calcul.' });
      return;
    }
    try {
      const result = await calculerNiveau({ niveau }).unwrap();
      setMessage({
        type: 'success',
        text: `Calcul terminé : ${result.etudiants_traites}/${result.total_etudiants} étudiants traités, ${result.total_semestres} résultats semestriels calculés.${result.errors.length > 0 ? ` (${result.errors.length} erreurs)` : ''}`,
      });
    } catch {
      setMessage({ type: 'error', text: 'Erreur lors du calcul des résultats.' });
    }
  };

  const getDecisionVariant = (decision: string): any => {
    switch (decision) {
      case 'Admis': return 'success';
      case 'Ajourné': return 'danger';
      default: return 'default';
    }
  };

  const getMentionVariant = (mention: string | null): any => {
    if (!mention) return 'default';
    if (mention === 'Très Bien') return 'success';
    if (mention === 'Bien') return 'info';
    return 'warning';
  };

  return (
    <div className="space-y-5">
      {/* Message */}
      {message && (
        <div className={`flex items-center justify-between p-3 rounded-lg text-sm ${
          message.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800' :
          message.type === 'error' ? 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800' :
          'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-800'
        }`}>
          <div className="flex items-center gap-2">
            {message.type === 'success' ? <CheckCircle size={16} /> :
             message.type === 'error' ? <XCircle size={16} /> :
             <AlertTriangle size={16} />}
            {message.text}
          </div>
          <button onClick={() => setMessage(null)} className="p-1 hover:opacity-70"><X size={14} /></button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Calculator size={28} className="text-blue-600 dark:text-blue-400" />
            Résultats et Calculs LMD
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
            Consultation et calcul des résultats académiques
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" icon={<RefreshCw size={16} />} onClick={() => refetch()} disabled={isLoading}>
            Actualiser
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={calculating ? <Spinner size="sm" /> : <Calculator size={16} />}
            onClick={handleCalculerResultats}
            disabled={calculating}
          >
            Calculer ({filters.niveau || 'Sélectionnez un niveau'})
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Taux de Réussite</p>
              <h3 className={`text-2xl font-bold ${stats.tauxReussite >= 50 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {stats.total > 0 ? `${stats.tauxReussite.toFixed(1)}%` : '—'}
              </h3>
            </div>
            <Award className="text-green-600 dark:text-green-400" size={28} />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Moyenne Générale</p>
              <h3 className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {stats.total > 0 ? stats.moyenneGenerale.toFixed(2) : '—'}
              </h3>
            </div>
            <TrendingUp className="text-blue-600 dark:text-blue-400" size={28} />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Admis</p>
              <h3 className="text-2xl font-bold text-green-700 dark:text-green-400">{stats.admis}</h3>
            </div>
            <div className="w-3 h-3 bg-green-600 rounded-full" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Ajournés</p>
              <h3 className="text-2xl font-bold text-red-700 dark:text-red-400">{stats.ajournes}</h3>
            </div>
            <div className="w-3 h-3 bg-red-600 rounded-full" />
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <form onSubmit={handleSearch} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2 relative">
              <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par matricule ou nom..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>

            <select
              value={filters.annee_academique}
              onChange={(e) => setFilters(f => ({ ...f, annee_academique: e.target.value }))}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Toutes les années</option>
              {annees.map((a: any) => (
                <option key={a.id} value={a.libelle}>{a.libelle}{a.est_active ? ' (active)' : ''}</option>
              ))}
            </select>

            <select
              value={filters.pole}
              onChange={(e) => setFilters(f => ({ ...f, pole: e.target.value, classe: '' }))}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tous les pôles</option>
              {['Polytechnique', 'Commerce', 'Juridique'].map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <select
              value={filters.niveau}
              onChange={(e) => setFilters(f => ({ ...f, niveau: e.target.value, classe: '' }))}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tous les niveaux</option>
              <option value="L1">L1</option>
              <option value="L2">L2</option>
              <option value="L3">L3</option>
              <option value="M1">M1</option>
              <option value="M2">M2</option>
            </select>

            <select
              value={filters.semestre}
              onChange={(e) => setFilters(f => ({ ...f, semestre: e.target.value }))}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tous les semestres</option>
              <option value="1">Semestre 1</option>
              <option value="2">Semestre 2</option>
            </select>

            <select
              value={filters.decision}
              onChange={(e) => setFilters(f => ({ ...f, decision: e.target.value }))}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Toutes décisions</option>
              <option value="admis">Admis</option>
              <option value="ajourne">Ajournés</option>
            </select>

            <select
              value={filters.classe}
              onChange={(e) => setFilters(f => ({ ...f, classe: e.target.value }))}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Toutes les classes</option>
              {(choixFormulaire?.classes || [])
                .filter(c => !filters.niveau || c.code.startsWith(filters.niveau))
                .map(c => (
                  <option key={c.code} value={c.code}>{c.label} ({c.count})</option>
                ))}
            </select>
          </div>

          <div className="flex items-center gap-3">
            <Button type="submit" variant="primary" size="sm" icon={<Search size={14} />}>
              Rechercher
            </Button>
            {(filters.niveau || filters.semestre || filters.decision || filters.classe || filters.pole || filters.annee_academique || searchQuery) && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => { setFilters({ niveau: '', semestre: '', decision: '', classe: '', pole: '', annee_academique: '' }); setSearchQuery(''); }}
              >
                Réinitialiser
              </Button>
            )}
          </div>
        </form>
      </Card>

      {/* Results Table */}
      <Card>
        {!hasRequiredFilter ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400 dark:text-gray-500">
            <Calculator size={40} className="mb-3 opacity-50" />
            <p className="font-medium text-gray-600 dark:text-gray-400">Sélectionnez un niveau ou une classe</p>
            <p className="text-sm mt-1">Les résultats s'afficheront après sélection d'un filtre</p>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner size="lg" />
            <span className="ml-3 text-gray-500 dark:text-gray-400">Chargement des résultats...</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase w-12">Rang</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Étudiant</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Niveau</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Classe</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Sem.</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Moyenne</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Crédits</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Mention</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Décision</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {resultats.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-6 py-16 text-center">
                      <Calculator size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                      <p className="text-gray-500 dark:text-gray-400 font-medium">Aucun résultat trouvé</p>
                      <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">
                        Sélectionnez un niveau et cliquez sur "Calculer" pour générer les résultats
                      </p>
                    </td>
                  </tr>
                ) : (
                  resultats.map((r) => {
                    const moy = r.moyenne_semestre != null ? parseFloat(String(r.moyenne_semestre)) : null;
                    // Fix 2026-09-17 : `annee_academique` est l'id de la FK ; le relevé
                    // attend le libellé, sinon il sort sans aucune note.
                    const annee = (r as any).annee_academique_libelle || filters.annee_academique || '';
                    const nonValide = moy != null && moy < 10;
                    return (
                      <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                        <td className="px-3 py-2.5 text-center">
                          {r.rang != null ? (
                            <span className="inline-flex items-center justify-center w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full text-xs font-bold text-gray-700 dark:text-gray-200">
                              {r.rang}
                            </span>
                          ) : <span className="text-gray-300">—</span>}
                          {r.effectif_classe != null && (
                            <div className="text-[9px] text-gray-400 mt-0.5">/ {r.effectif_classe}</div>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{r.etudiant_nom}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">{r.etudiant_matricule}</div>
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <Badge variant="info" size="sm">{r.etudiant_niveau}</Badge>
                        </td>
                        <td className="px-3 py-2.5 text-center text-sm text-gray-600 dark:text-gray-400">
                          {r.etudiant_classe || '—'}
                        </td>
                        <td className="px-3 py-2.5 text-center text-sm text-gray-700 dark:text-gray-300">
                          S{r.semestre}
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <span className={`text-base font-bold ${
                            moy != null && moy >= 10 ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                          }`}>
                            {moy != null ? moy.toFixed(2) : '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-center text-sm text-gray-700 dark:text-gray-300">
                          {r.credits_obtenus}/{r.credits_requis}
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          {r.mention ? (
                            <Badge variant={getMentionVariant(r.mention)} size="sm">{r.mention}</Badge>
                          ) : (
                            <span className="text-gray-300 dark:text-gray-600">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <Badge variant={getDecisionVariant(r.decision)} size="sm">{r.decision}</Badge>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => setModalReleve({
                                matricule: r.etudiant_matricule,
                                semestre: r.semestre,
                                annee: annee || new Date().getFullYear() + '-' + (new Date().getFullYear() + 1),
                              })}
                              className="p-1.5 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded"
                              title="Voir relevé provisoire"
                            >
                              <Eye size={14} />
                            </button>
                            <button
                              onClick={() => setModalSaisie({
                                etudiantId: r.etudiant,
                                matricule: r.etudiant_matricule,
                                nom: r.etudiant_nom,
                                niveau: r.etudiant_niveau,
                                semestre: r.semestre,
                                annee: annee || '',
                                option: r.etudiant_option,
                              })}
                              className="p-1.5 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30 rounded"
                              title="Saisir une note"
                            >
                              <Pencil size={14} />
                            </button>
                            {nonValide && (
                              <button
                                onClick={() => setModalRachat({
                                  etudiantId: r.etudiant,
                                  matricule: r.etudiant_matricule,
                                  nom: r.etudiant_nom,
                                  niveau: r.etudiant_niveau,
                                  classe: r.etudiant_classe,
                                  semestre: r.semestre,
                                  moyenneActuelle: moy,
                                })}
                                className="p-1.5 text-purple-600 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded"
                                title="Rachat intelligent"
                              >
                                <Wand2 size={14} />
                              </button>
                            )}
                            <button
                              onClick={() => {
                                setModalReleve({
                                  matricule: r.etudiant_matricule,
                                  semestre: r.semestre,
                                  annee: annee || new Date().getFullYear() + '-' + (new Date().getFullYear() + 1),
                                });
                                // impression différée : l'utilisateur clique ensuite sur Imprimer dans le modal
                              }}
                              className="p-1.5 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                              title="Imprimer le relevé"
                            >
                              <Printer size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Table Footer */}
        {resultats.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400">
            <span>{resultats.length} résultat(s) affiché(s)</span>
          </div>
        )}
      </Card>

      {/* Bouton flottant « Retour en haut » */}
      <ScrollToTop threshold={500} />

      {/* Modals */}
      {modalReleve && (
        <ModalReleveProvisoire
          matricule={modalReleve.matricule}
          semestre={modalReleve.semestre}
          anneeAcademique={modalReleve.annee}
          onClose={() => setModalReleve(null)}
        />
      )}
      {modalRachat && (
        <ModalRachatIntelligent
          {...modalRachat}
          onClose={() => setModalRachat(null)}
          onSuccess={() => {
            setMessage({ type: 'success', text: 'Rachat appliqué avec succès.' });
            refetch();
          }}
        />
      )}
      {modalSaisie && (
        <ModalSaisieNote
          etudiantId={modalSaisie.etudiantId}
          matricule={modalSaisie.matricule}
          nom={modalSaisie.nom}
          niveau={modalSaisie.niveau}
          semestre={modalSaisie.semestre}
          anneeAcademique={modalSaisie.annee}
          option={modalSaisie.option}
          onClose={() => setModalSaisie(null)}
          onSuccess={(text) => {
            setMessage({ type: 'success', text });
            refetch();
          }}
        />
      )}
    </div>
  );
};
